'use client';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { FC, useCallback, useState } from 'react';

import type { DownloadReport, SetShowSnackbar } from '../../types';

import { formatDateParkerStatus } from '../../utils';
import { ReportActionButton } from './reportActionButton';

interface OutstandingBalancesReportProps {
	facilityId: string;
	downloadReport: DownloadReport;
	setShowSnackbar: SetShowSnackbar;
	forTenant?: boolean;
	prefillAccountId?: string;
	prefillSubName?: string;
}

export const OutstandingBalancesReportForm: FC<OutstandingBalancesReportProps> = ({
	facilityId,
	downloadReport,
	setShowSnackbar,
	prefillAccountId,
	prefillSubName,
	forTenant = false,
}) => {
	const [reportType, setReportType] = useState<string>();
	const [accountId, setAccountId] = useState<string>();
	const [subName, setSubName] = useState<string>();
	const [asOfDate, setAsOfDate] = useState<string | null>();
	const [subscriptionStatus, setSubscriptionStatus] = useState<boolean>(false);
	const [currentBalances, setCurrentBalances] = useState<boolean>(true);
	const [contactInfo, setContactInfo] = useState<boolean>(false);
	const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
	const [isDownloadingCsv, setIsDownloadingCsv] = useState<boolean>(false);

	const reportTypes = [
		{ label: 'Outstanding Balances Report', value: 'OutstandingBalanceReport' },
		{ label: 'Outstanding Balances Summary Report', value: 'OutstandingBalanceSummaryReport' },
	];

	const isSummaryReport = reportType === 'OutstandingBalanceSummaryReport';

	const getDownloadParams = useCallback(() => {
		const baseParams = {
			...(accountId && { accountId }),
			...(subName && { subscriptionName: subName }),
			...(prefillAccountId && { accountId: prefillAccountId }),
			...(prefillSubName && { subscriptionName: prefillSubName }),
			...(facilityId && { facilityId }),
			asOfDate: formatDateParkerStatus(asOfDate as string),
		};

		const tenantCurrentBalances = forTenant ? true : currentBalances;

		const spreadSheetParams = isSummaryReport
			? { contactInfo, currentBalances: tenantCurrentBalances, subscriptionStatus }
			: { currentBalances: tenantCurrentBalances };

		return {
			...baseParams,
			...spreadSheetParams,
		};
	}, [
		facilityId,
		accountId,
		subName,
		asOfDate,
		contactInfo,
		currentBalances,
		subscriptionStatus,
		prefillSubName,
		prefillAccountId,
		isSummaryReport,
	]);

	const params = getDownloadParams();
	const handleValidation = useCallback(() => {
		const validations = [
			{
				message: `Please select a report type.`,
				failingCondition: !reportType,
			},
			{
				message: `Please select an As of Date.`,
				failingCondition: !asOfDate,
			},
		];
		const failingValidations = validations.filter(({ failingCondition }) => failingCondition);
		if (failingValidations.length) {
			setShowSnackbar({
				message: failingValidations[0].message,
				severity: 'error',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}
		return true;
	}, [reportType, setShowSnackbar, asOfDate]);

	const handleReset = useCallback(() => {
		setReportType('');
		setAccountId('');
		setAsOfDate(null);
		setSubscriptionStatus(false);
		setCurrentBalances(true);
		setContactInfo(false);
		setSubName('');
	}, []);

	return (
		<Box sx={{ padding: 3 }}>
			<Grid container spacing={2}>
				<Grid item xs={4}>
					<FormControl fullWidth variant='outlined' required>
						<InputLabel>Report Type</InputLabel>
						<Select
							label='Report Type'
							value={reportType || ''}
							onChange={(e) => setReportType(e.target.value)}
							data-cy='report-type-select-menu'
						>
							{reportTypes?.map((report) => (
								<MenuItem key={report.label} value={report.value}>
									{report.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={4}>
					<TextField
						fullWidth
						variant='outlined'
						label='Sub Name'
						disabled={forTenant}
						value={subName || prefillSubName || ''}
						onChange={(e) => setSubName(e.target.value)}
						inputProps={{ 'data-cy': 'sub-name-input' }}
					/>
				</Grid>
				<Grid item xs={4}>
					<TextField
						type='number'
						fullWidth
						variant='outlined'
						label='Account ID'
						disabled={forTenant}
						value={accountId || prefillAccountId || ''}
						onChange={(e) => setAccountId(e.target.value)}
						inputProps={{ 'data-cy': 'account-id-input' }}
						onKeyDown={(event) => {
							if (event?.key === '-' || event?.key === '+' || event?.key === 'e') {
								event.preventDefault();
							}
						}}
						onPaste={(event) => {
							event.preventDefault();

							const paste = event.clipboardData.getData('text');
							const cleanedPaste = /^[AC]*[-]*(\d+)$/.exec(paste);

							if (cleanedPaste) {
								setAccountId(cleanedPaste[1]);
							}
						}}
						InputProps={{
							startAdornment: <InputAdornment position='start'>AC-</InputAdornment>,
						}}
					/>
				</Grid>
				<Grid item xs={4}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='As of Date *'
							value={asOfDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'as-of-date-picker' },
								},
							}}
							onChange={setAsOfDate}
						/>
					</LocalizationProvider>
				</Grid>
			</Grid>
			<Box display='flex' justifyContent='center' marginTop={3}>
				<Box mr={3}>
					{isSummaryReport && (
						<FormControlLabel
							control={
								<Checkbox
									checked={subscriptionStatus}
									size='small'
									onChange={(e) => setSubscriptionStatus(e.target.checked)}
									data-cy='subscription-status-checkbox'
								/>
							}
							label='Subscription Status'
						/>
					)}
				</Box>
				<Box mr={3}>
					<FormControlLabel
						control={
							<Checkbox
								checked={currentBalances}
								size='small'
								disabled={forTenant}
								onChange={(e) => setCurrentBalances(e.target.checked)}
								data-cy='current-balances-checkbox'
							/>
						}
						label='Current Balances'
					/>
				</Box>
				{isSummaryReport && (
					<Box>
						<FormControlLabel
							control={
								<Checkbox
									checked={contactInfo}
									size='small'
									onChange={(e) => setContactInfo(e.target.checked)}
									data-cy='contact-information-checkbox'
								/>
							}
							label='Contact Information'
						/>
					</Box>
				)}
			</Box>

			<ReportActionButton
				downloadReport={(format: string) =>
					downloadReport(format, {
						reportType: reportType as string,
						params,
						handleValidation,
						setIsDownloadingCsv,
						setIsDownloadingPdf,
					})
				}
				isDownloadingPdf={isDownloadingPdf}
				isDownloadingCsv={isDownloadingCsv}
				onReset={handleReset}
				disableDownloadButtons={!facilityId}
			/>
		</Box>
	);
};
